import { type AxiosResponse } from "axios";

import { isWebview, mobile } from "@btnet/utilities-webview";

import {
    getBlobFromUrl,
    isSameOriginDownloadUrl,
    triggerAnchorDownload,
} from "utilities/file/file";

/**
 * Parses the "Content-Disposition" header to use the filename generated by the server
 * @param response The raw response generated by the server for a file download
 * @param fallbackFilename The filename to be used only when the "Content-Disposition" header fails to parse
 */
export const getFileNameFromHeader = (
    response: Response,
    fallbackFilename: string = "",
    alwaysUseFallbackFilename: boolean = false,
) => {
    if (alwaysUseFallbackFilename) {
        return fallbackFilename;
    }
    const content = response.headers.get("Content-Disposition");
    const matches = content?.match(/filename=(?<file>.*)/);

    // In some cases, System.Net.Http.Headers.ContentDispositionHeaderValue.FileName (used for HttpResponseMessage) string contains quotes (known bug).
    // These quotes must be removed before setting the download attribute. Otherwise, the file will not correctly download.
    // TODO: Remove replacing the quotes once the dotnet issue is resolved: https://github.com/dotnet/runtime/issues/32765
    return matches?.groups?.file.replace(/["]+/g, "") || fallbackFilename;
};

/**
 * Parses the "Content-Disposition" header to use the filename generated by the server
 * @param response The raw response generated by the server for a file download
 * @param fallbackFilename The filename to be used only when the "Content-Disposition" header fails to parse
 */
export const getFileNameFromAxiosResponse = (
    response: AxiosResponse,
    fallbackFilename: string = "",
    alwaysUseFallbackFilename: boolean = false,
): string => {
    if (alwaysUseFallbackFilename) {
        return fallbackFilename;
    }

    const content = response.headers["content-disposition"] as string | undefined;
    const matches = content?.match(/filename=(?<file>.*);/);

    // In some cases, System.Net.Http.Headers.ContentDispositionHeaderValue.FileName (used for HttpResponseMessage) string contains quotes (known bug).
    // These quotes must be removed before setting the download attribute. Otherwise, the file will not correctly download.
    // TODO: Remove replacing the quotes once the dotnet issue is resolved: https://github.com/dotnet/runtime/issues/32765
    return matches?.groups?.file.replace(/"+/g, "") ?? fallbackFilename;
};

export const handleWebviewDownload = (blob: Blob, filename: string): boolean => {
    if (isWebview(window) && window.onDownloadFile) {
        window.onDownloadFile({ blob, filename });
        return true;
    }
    return false;
};

/**
 * Downloads a same-origin URL, routing through the native bridge when we're in a
 * mobile webview. `<a download>` is a no-op in iOS WKWebView — there's no download
 * manager, so the click either silently does nothing or navigates the webview to
 * the file. Handing the blob to `onDownloadFile` puts it in the native share sheet
 * instead. Falls back to the anchor when the bridge is missing (older app versions,
 * or a normal browser).
 *
 * @returns false when the URL failed the same-origin check or the blob fetch threw.
 */
export const downloadUrlViaWebviewOrAnchor = async (
    url: string,
    filename?: string,
): Promise<boolean> => {
    if (!isSameOriginDownloadUrl(url)) {
        return false;
    }

    if (mobile.isWebview && mobile.onDownloadFile) {
        const { onDownloadFile } = mobile;
        try {
            const blob = await getBlobFromUrl(url);
            onDownloadFile({ blob, filename });
            return true;
        } catch {
            // Bridge path failed (fetch abort/network) — fall through to the anchor
            // rather than leaving the user with nothing.
        }
    }

    return triggerAnchorDownload(url, filename);
};

export const triggerBrowserDownload = (blob: Blob, filename: string) => {
    if (handleWebviewDownload(blob, filename)) {
        return;
    }

    const url = window.URL.createObjectURL(blob);
    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", url);
    linkElement.setAttribute("download", filename);

    // append to html page
    document.body.appendChild(linkElement);

    // force download
    linkElement.click();

    // clean up and remove the link
    linkElement.parentNode!.removeChild(linkElement);
    window.URL.revokeObjectURL(url);
};

/**
 * Downloads a file through using an external download link. Used for downloading Cross Origin Download URLs
 * (Ex: Vimeo Download URLs)
 * @param linkUrl URL to download
 */
export const generateExternalAttachmentFromLink = async (linkUrl: string, index: number) => {
    if (isWebview(window) && window.onDownloadFile) {
        const blob = await getBlobFromUrl(linkUrl);
        window.onDownloadFile({ blob, filename: `video-${index}.mp4` });
        return;
    }

    const iframeElement = document.createElement("iframe");
    iframeElement.setAttribute("src", linkUrl);
    iframeElement.setAttribute("id", `vimeoDownloadUrl-${index}`);

    // append to html page
    document.body.appendChild(iframeElement);

    // remove iframe, need to use set timeout to allow the video to start
    // downloading before removing the iframe.   Doing this as well for react so we just don't leave
    // these in the dom.
    void setTimeout(() => {
        iframeElement.parentNode!.removeChild(iframeElement);
    }, 3000);
};
